/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMemo } from "react";

import { CartItem, CartObject } from "../../cart/storage/cart.types";
import { Product, StockAvailabilityType } from "../storage";
import { getPriceInfo } from "../utils/price-info";
import { getShopAvailabilityDisplay, ShopAvailabilityDisplay } from "../utils/shop-availability";

export interface UseProductCardDisplayParams {
  item: Product | CartItem;
  isTfo: boolean | undefined;
  homeBranchId: string | undefined;
  /** Quantity used for price banding (in-cart qty for shop, line qty for cart). */
  priceQuantity?: number;
  noPriceAvailable: boolean;
  /** Caller resolves the source (line item vs. stocks slice). */
  stockAvailabilities: StockAvailabilityType[];
  cart: CartObject | undefined;
}

/**
 * Shared presentational mapping for `@farmlands/commerce` ProductCard.
 */
export const usePriceAndAvailability = ({
  item,
  isTfo,
  homeBranchId,
  priceQuantity,
  noPriceAvailable,
  stockAvailabilities,
  cart,
}: UseProductCardDisplayParams) => {
  const price = useMemo(
    () => getPriceInfo(item, isTfo, priceQuantity, noPriceAvailable),
    [item, isTfo, priceQuantity, noPriceAvailable]
  );

  const { stock, lifecycleNotice } = useMemo(
    (): ShopAvailabilityDisplay =>
      getShopAvailabilityDisplay({
        productId: item.productId,
        lifeCycleState: item.lifeCycleState,
        basePriceExcGst: item.basePriceExcGst,
        stockAvailabilityField: item.stockAvailability,
        stockAvailabilities,
        account: {
          homeBranchId,
          hasTfoAccess: isTfo,
        },
        cartDeliveryMode: cart?.cartDeliveryMode,
        pickupStore: cart?.pickupStore,
        cartItems: cart?.items,
        isUrgentDelivery: cart?.deliveryDetails?.urgent,
      }),
    [
      item.productId,
      item.lifeCycleState,
      item.basePriceExcGst,
      item.stockAvailability,
      stockAvailabilities,
      homeBranchId,
      isTfo,
      cart?.cartDeliveryMode,
      cart?.pickupStore,
      cart?.items,
      cart?.deliveryDetails?.urgent,
    ]
  );

  return { price, stock, lifecycleNotice };
};
